import React, { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import {
  useActiveInboxEmail,
  useCallOffsSearch,
  useLoadInboxEmails,
  useOpenCallOffPreview
} from 'src/models/callOffs/hooks';
import RejectInboxMailButton from 'src/shared/CallOffs/RejectInboxMailButton';
import BidPicker from 'src/shared/BidPicker';
import { ISelectedCallOff } from 'src/models/callOffs/types';
import { OverviewQp } from 'src/models/procurements/types';
import { useGetNewOverviewUrl } from 'src/models/procurements/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSearch } from '@fortawesome/pro-light-svg-icons';
import { Link } from 'react-router-dom';
import { useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import { IBidIdentifier } from 'src/models/bids/Bids/types';

interface IProps {}

export const PreviewHeader: FC<IProps> = () => {
  const { data: callOff } = useActiveInboxEmail();
  const searchVars = useCallOffsSearch();
  const { data: callOffsData } = useLoadInboxEmails(searchVars);
  const onOpenCallOffPreview = useOpenCallOffPreview();
  const currentSidebar = useCurrentSidebar();

  const nextCallOff = callOffsData && callOffsData.callOffs.find(co => co.id === callOff?.mailId)?.nextCallOff;

  const { t } = useTranslation();
  const getLinkOverview = useGetNewOverviewUrl();

  const onFinish = useCallback(() => {
    if (currentSidebar?.mode === SidebarMode.CALL_OFF_INFO) {
      onOpenCallOffPreview(nextCallOff);
    }
  }, [currentSidebar, nextCallOff, onOpenCallOffPreview]);

  const currentCallOff: ISelectedCallOff | undefined = useMemo(() => {
    if (!callOff) return;
    const bid: IBidIdentifier | undefined = !!callOff.bidsV2.length
      ? { id: callOff.bidsV2[0].id, wsId: callOff.bidsV2[0].workspace.id }
      : undefined;

    const linkedBid = !!callOff.linkedBidsV2.length
      ? {
          id: callOff.linkedBidsV2[0].id,
          wsId: callOff.linkedBidsV2[0].workspace.id,
          originId: callOff.mailId
        }
      : undefined;
    return {
      id: callOff.mailId,
      linkedBid,
      bid
    };
  }, [callOff]);

  const href = useMemo(() => {
    const placeHolderBid = currentCallOff?.bid || currentCallOff?.linkedBid;
    return (
      placeHolderBid &&
      getLinkOverview({
        [OverviewQp.WorkspaceId]: placeHolderBid.wsId,
        [OverviewQp.PreviewId]: placeHolderBid.id,
        [OverviewQp.PreviewMode]: SidebarMode.BID_INFO
      })
    );
  }, [currentCallOff, getLinkOverview]);

  return (
    <>
      {callOff && (
        <>
          <h4 className={styles.heading}>
            <span className={styles.title}>{callOff.subject}</span>
          </h4>
          <div className={styles.subTitle}>
            <span className={styles.subTitleLabel}>{t('Common.from')}</span>
            <span className={styles.subTitleValue}>{callOff.originalSender}</span>
          </div>
          <div className={styles.container}>
            <div className={styles.headingActions}>
              {currentCallOff && (
                <div className={styles.headingAction}>
                  <BidPicker
                    selectedCallOffs={[currentCallOff]}
                    placeHolderBid={currentCallOff.bid || currentCallOff.linkedBid}
                    mode={'full'}
                    eventSource={'CallOffPreview'}
                    onChange={onFinish}
                  />
                </div>
              )}
              <div className={styles.headingAction}>
                <RejectInboxMailButton selectedMailIds={[callOff.mailId]} mode={'full'} onComplete={onFinish} />
              </div>
            </div>
            {href && (
              <Link className={styles.button} to={href}>
                <FontAwesomeIcon icon={faFileSearch} className={styles.buttonIcon} />
                <span className={styles.buttonLabel}>{t('CallOffs.openBid')}</span>
              </Link>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PreviewHeader;
